import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, IconButton, Tooltip, Typography, styled } from "@mui/material";
import { Close as CloseIcon, Insights as InsightsIcon } from "@mui/icons-material";

// Types
import { AiUsageRecordRow, AiUsageThreadDetail } from "../types/MeGenTypes";
import { useIntl } from "react-intl";
import ModAiUsageBreakdown from "./ModAiUsageBreakdown";

import AiRatePriceCell, { EstimatedBadge } from "./AiRatePriceCell";
import { TIMELINE_PAGE_SIZE, buildRunCostRows, costOfUsage, lineOfTimelineRow, makeRecordCostInfoFn, usageTextOf } from "./aiModelCost";
import { PRICE_BOOK, isRateOverridden, resolveRate, variantLabelOf } from "./aiModelRates";
import { PRICE_BOOK_PATH } from "./aiPriceBook";
import { formatMetric, formatMoney, serviceLabelOf, toLocalDateTime } from "./aiUsagePivot";
import { useAiModelRates } from "./useAiModelRates";
import AiUsageModelLabel from "./AiUsageModelLabel";
import FfmpegUsageSummary from "./FfmpegUsageSummary";
import { FfmpegAllocationCost, FfmpegAllocationTime } from "./FfmpegAllocationValue";
import { formatTaskCost, recordOfFfmpegTask } from "./ffmpegUsage";

/**
 * ModMeGenAiUsage — modal สรุปค่าใช้จ่าย AI ของ 1 run (thread)
 *
 * ใช้ซ้ำจาก 4 ที่: CardGenerateScript, CardGenerateVideo, CardGenerateDirectScript และ CardMeGenCostMain
 * - โหลดเองจาก `/ai-usage/thread/{id}` ผ่าน prop `onFetchUsage` เมื่อ `open` เป็น true
 * - ราคาที่แสดงเป็น **อ่านอย่างเดียว** แก้ได้ที่แท็บ "ราคา Model" เท่านั้น (prop `onEditRates` มีเฉพาะตอนเปิดจาก MeGen Cost)
 * - timeline ของ backend ถูก cap ที่ TIMELINE_PAGE_SIZE แถว ถ้าเกินจะ fallback ไปคิดจาก `by_model` (ดู buildRunCostRows)
 * - รายละเอียดกฎการคิดเงินและศัพท์: ดู common/AI_USAGE.md
 */

// ─── Styled Components ───────────────────────────────────────────────────────

const Overlay = styled(Box)({
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(15, 23, 42, 0.5)",
  backdropFilter: "blur(4px)",
});

const ModalCard = styled(Box)({
  backgroundColor: "white",
  borderRadius: "1rem",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  width: "100%",
  maxWidth: "920px",
  maxHeight: "85vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
});

const SectionLabel = styled(Typography)({
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#94a3b8",
  marginBottom: "0.5rem",
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

// created_at เป็น UTC — แสดงเป็นเวลาท้องถิ่น (ไทย) พร้อมวินาที
const formatDateTime = (iso: string | null): string => toLocalDateTime(iso, { seconds: true });

const thTd: React.CSSProperties = {
  padding: "6px 10px",
  fontSize: "0.75rem",
  borderBottom: "1px solid #f1f5f9",
  textAlign: "left",
  whiteSpace: "nowrap",
  verticalAlign: "top",
};
const thStyle: React.CSSProperties = { ...thTd, fontSize: "0.68rem", color: "#64748b", backgroundColor: "#f8fafc", verticalAlign: "middle" };
const numTd: React.CSSProperties = { ...thTd, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const footTd: React.CSSProperties = { ...thTd, fontWeight: 700, borderTop: "1px solid #e2e8f0" };

const unique = (items: string[]): string[] => items.filter((x, i) => items.indexOf(x) === i);

// ─── Component ───────────────────────────────────────────────────────────────

type ModMeGenAiUsageProps = {
  open: boolean;
  threadId: string | null;
  threadTitle?: string;
  userNameMap?: Record<string, string>; // user_id -> ชื่อ (จาก allOaUsersList) — fallback เมื่อ detail.user_names ไม่มี
  onClose: () => void;
  onFetchUsage?: (
    threadId: string,
    onSuccess: (detail: AiUsageThreadDetail) => void,
    onError: (message: string) => void
  ) => void;
  onEditRates?: () => void; // เปิดแท็บ ราคา Model (มีเฉพาะเมื่อเปิด modal จากหน้า MeGen Cost)
  dashboardRecords?: AiUsageRecordRow[]; // Complete selected range/filter; run modal behavior otherwise unchanged.
};

const ModMeGenAiUsage: React.FC<ModMeGenAiUsageProps> = (props) => {
  const intl = useIntl();
  const [detail, setDetail] = useState<AiUsageThreadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const ffmpegRecords = useMemo(() => (detail?.ffmpeg_tasks || []).map(recordOfFfmpegTask), [detail]);

  // ราคาต่อ model — override จาก useAiModelRates (เก็บใน localStorage ของ browser นี้ sync ข้ามแท็บ)
  // ที่นี่แสดงอย่างเดียว แก้ราคาได้ที่แท็บ "ราคา Model" ใน MeGen Cost เท่านั้น
  const rates = useAiModelRates();

  useEffect(() => {
    if (!props.open || !props.threadId || props.dashboardRecords) return;
    let current = true;

    setDetail(null);
    setError(null);
    setLoading(true);

    props.onFetchUsage?.(
      props.threadId,
      (data) => {
        if (!current) return;
        setDetail(data);
        setLoading(false);
      },
      (message) => {
        if (!current) return;
        setError(message);
        setLoading(false);
      }
    );
    return () => { current = false; };
    // เปิด modal ครั้งใหม่ / เปลี่ยน thread → โหลดใหม่
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open, props.threadId, props.dashboardRecords]);

  // แถว = model × variant (resolution/เสียง) — ยอดรวมต้องแยกตาม model เพราะราคาต่างกัน
  const summary = useMemo(
    () => (detail ? buildRunCostRows(detail, rates.overrides) : null),
    [detail, rates.overrides]
  );
  const costInfoOf = useMemo(() => makeRecordCostInfoFn(rates.overrides), [rates.overrides]);

  // ค่าใช้จ่ายราย call ใน timeline
  const timelineCosts = useMemo(
    () =>
      (detail?.timeline || []).map((t) => {
        const line = lineOfTimelineRow(t);
        return costOfUsage(line, resolveRate(line, rates.overrides).rate);
      }),
    [detail, rates.overrides]
  );
  const timelineItems = useMemo(() => {
    const items: Array<{ source: "ai" | "ffmpeg"; index: number; createdAt: string | null }> = [];
    (detail?.timeline || []).forEach((item, index) => items.push({ source: "ai", index, createdAt: item.created_at }));
    ffmpegRecords.forEach((item, index) => items.push({ source: "ffmpeg", index, createdAt: item.created_at }));
    const timeOf = (value: string | null): number => {
      const time = value ? new Date(value).getTime() : 0;
      return Number.isFinite(time) ? time : 0;
    };
    return items.sort((a, b) => timeOf(b.createdAt) - timeOf(a.createdAt) || a.source.localeCompare(b.source) || a.index - b.index);
  }, [detail, ffmpegRecords]);

  if (!props.open) return null;
  if (props.dashboardRecords) return <ModAiUsageBreakdown open={props.open} title={props.threadTitle || props.threadId || "AI Usage"} subtitle={intl.formatMessage({ id: "megen.cost.selectedLogs" })} records={props.dashboardRecords} userNameMap={props.userNameMap} onClose={props.onClose} />;

  const totals = detail?.totals;
  const rows = summary?.rows || [];
  const totalCalls = rows.reduce((s, r) => s + r.calls, 0);
  const totalErrors = rows.reduce((s, r) => s + r.errors, 0);
  const estimateReasons = unique(rows.reduce<string[]>((acc, r) => acc.concat(r.result.reasons), []));
  const firstMissingIndex = rows.findIndex((r) => r.result.missingRate);
  const hasGpuHour = rows.some((r) => r.result.rate.unit === "gpu_hour");

  // ชื่อจาก backend (user_names ใน detail) ชนะ — fallback map จาก props แล้วค่อย id
  const nameOf = (userId: string | null | undefined): string =>
    (userId && (detail?.user_names?.[userId] || props.userNameMap?.[userId])) || userId || "-";

  const kpiBox = (
    label: string,
    value: React.ReactNode,
    opts?: { danger?: boolean; accent?: boolean; extra?: React.ReactNode }
  ) => (
    <Box
      sx={{
        px: 1.5, py: 0.75, borderRadius: "0.5rem", minWidth: "110px",
        backgroundColor: opts?.danger ? "#fef2f2" : opts?.accent ? "#fdf2f8" : "#f8fafc",
        border: `1px solid ${opts?.danger ? "#fecaca" : opts?.accent ? "#f9a8d4" : "#e2e8f0"}`,
      }}
    >
      <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8" }}>{label}</Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
        <Typography
          sx={{
            fontSize: opts?.accent ? "1.15rem" : "0.9rem", fontWeight: 700, fontVariantNumeric: "tabular-nums",
            color: opts?.danger ? "#dc2626" : opts?.accent ? "#be185d" : "#1e293b",
          }}
        >
          {value}
        </Typography>
        {opts?.extra}
      </Box>
    </Box>
  );

  return (
    <Overlay id="megen-ai-usage-modal" onClick={props.onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
          <InsightsIcon sx={{ color: "#db2777" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b" }}>
              AI Usage{props.threadTitle ? `: ${props.threadTitle}` : ""}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#94a3b8", overflow: "hidden", textOverflow: "ellipsis" }}>
              {props.threadId}
              {detail?.users?.length ? ` · users: ${detail.users.map(nameOf).join(", ")}` : ""}
            </Typography>
          </Box>
          <IconButton size="small" onClick={props.onClose} id="megen-ai-usage-modal-close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3, overflowY: "auto" }}>
          {!loading && !error && <FfmpegUsageSummary records={ffmpegRecords} costInfoOf={costInfoOf} />}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} sx={{ color: "#db2777" }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ p: 2, backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "0.5rem" }}>
              <Typography sx={{ fontSize: "0.875rem", color: "#dc2626" }}>{error}</Typography>
            </Box>
          )}

          {!loading && !error && detail && summary && (
            <>
              {/* KPI: ค่าใช้จ่ายรวม (Σ แถวราย model) + calls + errors */}
              <SectionLabel>ยอดรวมทั้ง Run</SectionLabel>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "stretch", gap: 1, mb: 1 }}>
                {kpiBox("ค่าใช้จ่ายรวม (USD)", formatMoney(summary.total), {
                  accent: true,
                  extra: summary.estimated ? <EstimatedBadge reasons={estimateReasons} /> : undefined,
                })}
                {kpiBox(intl.formatMessage({ id: "megen.cost.allJobs" }), `${formatMetric(totalCalls)} ครั้ง`)}
                {kpiBox("Errors", `${formatMetric(totalErrors)} ครั้ง`, { danger: totalErrors > 0 })}
                {summary.missingCount > 0 && (
                  <Box
                    id="megen-ai-usage-missing-rate"
                    onClick={() =>
                      document.getElementById(`megen-ai-usage-row-${firstMissingIndex}`)?.scrollIntoView({ block: "nearest" })
                    }
                    sx={{
                      px: 1.5, py: 0.75, borderRadius: "0.5rem", backgroundColor: "#fef3c7", border: "1px solid #fcd34d",
                      cursor: "pointer", display: "flex", alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.75rem", color: "#b45309", fontWeight: 600 }}>
                      ⚠ {summary.missingCount} model ยังไม่ได้ตั้งราคา — ตั้งราคาที่แท็บ ราคา Model หรือแก้ใน aiPriceBook.json
                    </Typography>
                  </Box>
                )}
                {summary.unknownQuantityCount > 0 && (
                  <Tooltip title="log ของ call เหล่านี้ไม่มี duration_ms → คิดค่า GPU ไม่ได้ (นับเป็น $0)">
                    <Box
                      id="megen-ai-usage-unknown-gpu-time"
                      sx={{ px: 1.5, py: 0.75, borderRadius: "0.5rem", backgroundColor: "#f1f5f9", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", cursor: "help" }}
                    >
                      <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 600 }}>
                        ⓘ {summary.unknownQuantityCount} แถว GPU ไม่มีเวลาเครื่องใน log
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <Typography sx={{ fontSize: "0.72rem", color: "#64748b", mb: 3 }}>
                ปริมาณรวม:{" "}
                {usageTextOf({
                  images: totals?.images || 0,
                  video_seconds: totals?.video_seconds || 0,
                  input_video_seconds: totals?.input_video_seconds || 0,
                  audio_seconds: totals?.audio_seconds || 0,
                  input_tokens: totals?.input_tokens || 0,
                  output_tokens: totals?.output_tokens || 0,
                })}
              </Typography>

              {/* By model × variant (ราคาอ่านอย่างเดียว — แก้ที่แท็บ ราคา Model) */}
              <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <SectionLabel>แยกตาม AI Model</SectionLabel>
                {props.onEditRates ? (
                  <Typography
                    id="megen-ai-usage-edit-rates"
                    onClick={props.onEditRates}
                    sx={{ fontSize: "0.72rem", color: "#db2777", cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}
                  >
                    แก้ราคาที่แท็บ ราคา Model →
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>แก้ราคาได้ที่ MeGen Cost › แท็บ ราคา Model</Typography>
                )}
              </Box>
              <Box sx={{ overflowX: "auto", border: "1px solid #f1f5f9", borderRadius: "0.5rem", mb: 1 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "780px" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Service</th>
                      <th style={thStyle}>Model</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Calls</th>
                      <th style={thStyle}>ปริมาณที่ใช้</th>
                      <th style={thStyle}>ราคาต่อหน่วย (USD)</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>ค่าใช้จ่าย</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const rate = row.result.rate;
                      const allErrors = rate.unit !== "gpu_hour" && row.calls > 0 && row.errors >= row.calls; // GPU Hub คิด error ด้วย
                      const variantLabel = variantLabelOf(row.variant);
                      return (
                        <tr key={row.key} id={`megen-ai-usage-row-${i}`} style={{ backgroundColor: row.result.missingRate ? "#fffbeb" : undefined }}>
                          <td style={thTd}>{serviceLabelOf(row.service)}</td>
                          <td style={{ ...thTd, fontFamily: "monospace" }}>
                            {row.model}
                            {variantLabel && (
                              <span style={{ marginLeft: 6, fontSize: "0.68rem", color: "#94a3b8" }}>{variantLabel}</span>
                            )}
                          </td>
                          <td style={numTd}>{formatMetric(row.calls)}</td>
                          <td style={thTd}>{row.service === "ffmpeg" ? <FfmpegAllocationTime result={row.result} seconds={row.usage.process_seconds || 0} /> : usageTextOf(row.usage, rate.unit === "gpu_hour")}</td>
                          <td style={{ ...thTd, whiteSpace: "normal", minWidth: 220 }}>
                            <AiRatePriceCell
                              idPrefix={`megen-ai-usage-rate-${row.provider}-${row.model}-${row.variant}`}
                              rate={rate}
                              result={row.result}
                              fallbackFrom={row.fallbackFrom}
                              isOverridden={(v) => isRateOverridden(rate, v, rates.isOverridden)}
                            />
                          </td>
                          <td style={{ ...numTd, fontWeight: 600, color: row.result.missingRate ? "#b45309" : "#1e293b" }}>
                            {row.service === "ffmpeg" ? (
                              <FfmpegAllocationCost result={row.result} />
                            ) : row.result.missingRate ? (
                              "–"
                            ) : row.result.quantityUnknown && row.result.cost === 0 ? (
                              <Tooltip title="log ไม่มีเวลาเครื่อง (duration_ms) → คิดค่า GPU ไม่ได้"><span>–</span></Tooltip>
                            ) : allErrors ? (
                              <Tooltip title="คิดเฉพาะ call ที่สำเร็จ — แถวนี้ error ทั้งหมด"><span>–</span></Tooltip>
                            ) : (
                              formatMoney(row.result.cost)
                            )}
                          </td>
                          <td style={{ ...numTd, color: row.errors ? "#dc2626" : "#94a3b8", fontWeight: row.errors ? 700 : 400 }}>
                            {row.errors}
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr><td style={thTd} colSpan={7}>ไม่มีข้อมูล</td></tr>
                    )}
                  </tbody>
                  {rows.length > 0 && (
                    <tfoot>
                      <tr style={{ backgroundColor: "#f8fafc" }}>
                        <td style={footTd} colSpan={2}>
                          รวม <span style={{ fontWeight: 400, color: "#94a3b8" }}>· คิดเฉพาะ call ที่สำเร็จ (ไม่คิดเงินรายการ error){hasGpuHour ? " · GPU Hub คิดตามเวลาเครื่องรวม error" : ""}</span>
                        </td>
                        <td style={{ ...footTd, textAlign: "right" }}>{formatMetric(totalCalls)}</td>
                        <td style={footTd} colSpan={2} />
                        <td id="megen-ai-usage-total-cost" style={{ ...footTd, textAlign: "right", color: "#be185d" }}>
                          {formatMoney(summary.total)}
                        </td>
                        <td style={{ ...footTd, textAlign: "right", color: totalErrors ? "#dc2626" : "#94a3b8" }}>
                          {totalErrors}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </Box>
              <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8", mb: 3 }}>
                ราคาจาก price book {PRICE_BOOK_PATH}{PRICE_BOOK.updated ? ` (อัปเดต ${PRICE_BOOK.updated})` : ""} · Local = ฟรี · GPU Hub คิดตามชั่วโมงเวลาเครื่อง ·
                ราคาที่แก้ในแท็บ ราคา Model ของ MeGen Cost (บันทึกใน browser นี้) มีผลที่นี่ด้วย
              </Typography>

              {/* Timeline */}
              <SectionLabel>Timeline การใช้งาน (ใหม่ → เก่า{detail.timeline.length >= TIMELINE_PAGE_SIZE ? ` · AI แสดง ${TIMELINE_PAGE_SIZE} รายการล่าสุด` : ""})</SectionLabel>
              <Box role="list" sx={{ maxHeight: "260px", overflowY: "auto", border: "1px solid #f1f5f9", borderRadius: "0.5rem" }}>
                {timelineItems.map((item) => {
                  if (item.source === "ffmpeg") {
                    const record = ffmpegRecords[item.index];
                    const task = record.ffmpeg_task!;
                    const cost = costInfoOf(record).result;
                    const outputName = task.output_key.split("/").pop() || task.id;
                    return (
                      <Box key={record.id} role="listitem" sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1,
                        px: 1.5, py: 0.75, borderBottom: "1px solid #f1f5f9", backgroundColor: task.status === "error" ? "#fef2f2" : "transparent" }}>
                        <Typography sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#64748b", whiteSpace: "nowrap" }}>
                          {formatDateTime(record.created_at)}
                        </Typography>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#334155" }}>
                          {outputName.startsWith("final_podcast") ? "FFmpeg Merge" : "FFmpeg Render Segment"} (GPUHub) · {outputName}
                        </Typography>
                        {record.user_id && <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>โดย {nameOf(record.user_id)}</Typography>}
                        <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>
                          {intl.formatMessage({ id: "megen.cost.ffmpegAllocated" }, { seconds: task.allocated_instance_seconds === null ? "—" : formatMetric(task.allocated_instance_seconds) })}
                          {task.gpu_compute_seconds !== null ? ` · ${intl.formatMessage({ id: "megen.cost.ffmpegCompute" }, { seconds: formatMetric(task.gpu_compute_seconds) })}` : ""}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#be185d", fontVariantNumeric: "tabular-nums" }}>
                          {cost.knownAllocationCount ? formatMoney(cost.cost) : formatTaskCost(task, intl.formatMessage({ id: "megen.cost.ffmpegCostUnknown" }))}
                        </Typography>
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: task.status === "error" ? "#dc2626" : "#64748b" }}>
                          {task.status || "unknown"}
                        </Typography>
                        {task.status === "error" && <Box component="details" sx={{ flexBasis: "100%", fontSize: "0.72rem", color: "#991b1b" }}>
                          <Box component="summary" sx={{ cursor: "pointer" }}>{intl.formatMessage({ id: "megen.cost.ffmpegErrorDetails" })}</Box>
                          <Box component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxHeight: 180, overflowY: "auto", mt: 0.5 }}>
                            {task.error_message || intl.formatMessage({ id: "megen.cost.ffmpegErrorMissing" })}
                          </Box>
                        </Box>}
                      </Box>
                    );
                  }
                  const t = detail.timeline[item.index];
                  const cost = timelineCosts[item.index];
                  return (
                    <Box key={`ai:${item.index}`} role="listitem"
                      sx={{
                        display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 1,
                        px: 1.5, py: 0.75, borderBottom: "1px solid #f8fafc",
                        backgroundColor: t.status !== "success" ? "#fef2f2" : "transparent",
                      }}>
                      <Typography sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#64748b", whiteSpace: "nowrap" }}>
                        {formatDateTime(t.created_at)}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#334155" }}>
                        {serviceLabelOf(t.service)} · <AiUsageModelLabel provider={t.provider} model={t.model} videoDuration={t.service === "video" ? t.video_duration : undefined} />
                      </Typography>
                      {t.user_id && (
                        <Typography sx={{ fontSize: "0.7rem", color: "#64748b" }}>โดย {nameOf(t.user_id)}</Typography>
                      )}
                      <Typography sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                        {usageTextOf(lineOfTimelineRow(t))}
                        {t.resolution ? ` · ${t.resolution}` : ""}
                        {t.requested_duration ? ` · ขอ ${formatMetric(t.requested_duration)} วิ` : ""}
                        {t.duration_ms ? ` · ${formatMetric(t.duration_ms / 1000)} วิ (process)` : ""}
                      </Typography>
                      {cost && !cost.missingRate && (t.status === "success" || cost.cost > 0) && (
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "#be185d", fontVariantNumeric: "tabular-nums" }}>
                          {formatMoney(cost.cost)}
                        </Typography>
                      )}
                      {t.status !== "success" && (
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#dc2626" }}>
                          {t.status}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
                {timelineItems.length === 0 && (
                  <Typography sx={{ p: 2, fontSize: "0.8rem", color: "#94a3b8" }}>ไม่มีข้อมูล</Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </ModalCard>
    </Overlay>
  );
};

export default ModMeGenAiUsage;
